import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Loader2, Save, Palette, Type, ImageIcon, ShieldCheck, Settings2 } from "lucide-react";
import { toast } from "sonner";

const toLines = (a) => (Array.isArray(a) ? a.join("\n") : "");
const fromLines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

function Section({ icon: Icon, title, desc, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 rounded-lg bg-[#F3E8FF] flex items-center justify-center text-[#581C87]"><Icon className="h-4 w-4" /></div>
        <div><h3 className="font-heading font-bold">{title}</h3><p className="text-xs text-gray-500">{desc}</p></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

const Txt = ({ label, val, set, ...p }) => (
  <div className="space-y-1.5"><Label className="text-xs">{label}</Label>
    <Input value={val ?? ""} onChange={(e) => set(e.target.value)} className="focus-visible:ring-[#581C87]" {...p} /></div>
);
const Num = ({ label, val, set, ...p }) => (
  <div className="space-y-1.5"><Label className="text-xs">{label}</Label>
    <Input type="number" value={val ?? 0} onChange={(e) => set(parseInt(e.target.value || "0", 10))} className="focus-visible:ring-[#581C87]" {...p} /></div>
);
const Area = ({ label, val, set, ...p }) => (
  <div className="space-y-1.5"><Label className="text-xs">{label}</Label>
    <Textarea value={val ?? ""} onChange={(e) => set(e.target.value)} className="focus-visible:ring-[#581C87]" {...p} /></div>
);
const Bool = ({ label, val, set, testid }) => (
  <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5">
    <Label className="text-xs cursor-pointer">{label}</Label>
    <Switch checked={!!val} onCheckedChange={set} data-testid={testid} /></div>
);

export default function MediaSettings() {
  const [c, setC] = useState(null);
  const [sizes, setSizes] = useState(["1080x1350", "1080x1080"]);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = () => api.get("/marketing/media-settings").then((r) => {
    setC(r.data.creative); setSizes(r.data.supported_sizes); setUpdatedAt(r.data.updated_at);
  }).catch(() => setC(false));
  useEffect(() => { load(); }, []);

  const upd = (cat, key, v) => setC((s) => ({ ...s, [cat]: { ...s[cat], [key]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/marketing/media-settings", { creative: c });
      setC(data.creative); setUpdatedAt(data.updated_at);
      toast.success(data.note || "Media settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  if (c === null) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#581C87]" /></div>;
  if (c === false) return <p className="text-sm text-red-600">Failed to load media settings.</p>;

  const g = c.general, t = c.text, b = c.brand, im = c.image, v = c.validation;
  return (
    <div data-testid="media-settings-page" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold flex items-center gap-2"><Settings2 className="h-5 w-5 text-[#581C87]" /> Media Settings</h2>
          <p className="text-sm text-gray-500 mt-1">Global visual standards for social graphics. Changes apply only to future or manually re-rendered media — existing media is unchanged.</p>
          {updatedAt && <p className="text-xs text-gray-400 mt-0.5">Last updated {new Date(updatedAt).toLocaleString()}</p>}
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="save-media-settings-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Save Settings</>}
        </Button>
      </div>

      <Section icon={Settings2} title="General" desc="Default method, size and creative direction">
        <div className="space-y-1.5"><Label className="text-xs">Default Method</Label>
          <Select value={g.method} onValueChange={(x) => upd("general", "method", x)}>
            <SelectTrigger data-testid="setting-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Programmatic Template">Programmatic Template — No AI charge</SelectItem>
              <SelectItem value="Generate AI Background">Generate AI Background — Paid OpenAI</SelectItem>
            </SelectContent></Select></div>
        <div className="space-y-1.5"><Label className="text-xs">Default Size</Label>
          <Select value={g.size} onValueChange={(x) => upd("general", "size", x)}>
            <SelectTrigger data-testid="setting-size"><SelectValue /></SelectTrigger>
            <SelectContent>{sizes.map((s) => <SelectItem key={s} value={s}>{s} {s === "1080x1350" ? "(4:5)" : "(1:1)"}</SelectItem>)}</SelectContent></Select></div>
        <Txt label="Visual Style" val={g.style} set={(x) => upd("general", "style", x)} />
        <Area label="Visual Instructions" val={g.visual_instructions} set={(x) => upd("general", "visual_instructions", x)} />
        <Area label="Preferred Subjects (one per line)" val={toLines(g.subjects)} set={(x) => upd("general", "subjects", fromLines(x))} />
        <Area label="Preferred Environments (one per line)" val={toLines(g.environments)} set={(x) => upd("general", "environments", fromLines(x))} />
        <Area label="Prohibited Imagery (one per line)" val={toLines(g.prohibited_image)} set={(x) => upd("general", "prohibited_image", fromLines(x))} />
        <Area label="Prohibited Claims (one per line)" val={toLines(g.prohibited_claims)} set={(x) => upd("general", "prohibited_claims", fromLines(x))} />
      </Section>

      <Section icon={Type} title="Text Limits" desc="Word / line / font-size limits enforced by legibility validation">
        <Num label="Headline max words" val={t.headline_max_words} set={(x) => upd("text", "headline_max_words", x)} data-testid="setting-headline-max-words" />
        <Num label="Headline max lines" val={t.headline_max_lines} set={(x) => upd("text", "headline_max_lines", x)} />
        <Num label="Headline min font (px)" val={t.headline_min_px} set={(x) => upd("text", "headline_min_px", x)} />
        <Num label="Supporting line max words" val={t.support_max_words} set={(x) => upd("text", "support_max_words", x)} />
        <Num label="Supporting min font (px)" val={t.support_min_px} set={(x) => upd("text", "support_min_px", x)} />
        <Num label="CTA max words (excl URL)" val={t.cta_max_words} set={(x) => upd("text", "cta_max_words", x)} />
        <Num label="CTA min font (px)" val={t.cta_min_px} set={(x) => upd("text", "cta_min_px", x)} />
        <Num label="Brand identifier min font (px)" val={t.brand_min_px} set={(x) => upd("text", "brand_min_px", x)} />
        <Num label="Total displayed max words" val={t.total_max_words} set={(x) => upd("text", "total_max_words", x)} />
        <Num label="Safe margin (px)" val={t.safe_margin_px} set={(x) => upd("text", "safe_margin_px", x)} />
        <Txt label="Alignment" val={t.alignment} set={(x) => upd("text", "alignment", x)} />
        <Txt label="Text position" val={t.position} set={(x) => upd("text", "position", x)} />
      </Section>

      <Section icon={Palette} title="Brand" desc="Colors, fonts, CTA and logo placement">
        <Area label="Brand Colors (one per line)" val={toLines(b.colors)} set={(x) => upd("brand", "colors", fromLines(x))} />
        <Txt label="Font" val={b.font} set={(x) => upd("brand", "font", x)} />
        <Txt label="Font Weights" val={b.weights} set={(x) => upd("brand", "weights", x)} />
        <Txt label="Default CTA" val={b.cta_default} set={(x) => upd("brand", "cta_default", x)} />
        <Txt label="Logo Placement" val={b.logo_placement} set={(x) => upd("brand", "logo_placement", x)} />
        <Bool label="Use approved brand assets" val={b.use_approved_assets} set={(x) => upd("brand", "use_approved_assets", x)} testid="setting-use-assets" />
      </Section>

      <Section icon={ImageIcon} title="Image Preferences" desc="Photography style, allowed sources and things to avoid">
        <Txt label="Representation" val={im.representation} set={(x) => upd("image", "representation", x)} />
        <Txt label="Professional Setting" val={im.professional_setting} set={(x) => upd("image", "professional_setting", x)} />
        <Txt label="Photography / Mood" val={im.photography_mood} set={(x) => upd("image", "photography_mood", x)} />
        <Area label="Avoid (one per line)" val={toLines(im.avoid)} set={(x) => upd("image", "avoid", fromLines(x))} />
        <Bool label="Allow photographs" val={im.allow_photo} set={(x) => upd("image", "allow_photo", x)} testid="setting-allow-photo" />
        <Bool label="Allow brand assets" val={im.allow_brand_asset} set={(x) => upd("image", "allow_brand_asset", x)} testid="setting-allow-brand-asset" />
        <Bool label="Allow AI background (paid)" val={im.allow_ai_background} set={(x) => upd("image", "allow_ai_background", x)} testid="setting-allow-ai" />
      </Section>

      <Section icon={ShieldCheck} title="Validation" desc="Legibility gates applied to every render">
        <Bool label="Mobile legibility check" val={v.mobile_legibility} set={(x) => upd("validation", "mobile_legibility", x)} testid="setting-legibility" />
        <Bool label="Contrast check" val={v.contrast} set={(x) => upd("validation", "contrast", x)} />
        <Bool label="Safe margin check" val={v.safe_margin} set={(x) => upd("validation", "safe_margin", x)} />
        <Bool label="Overflow check" val={v.overflow} set={(x) => upd("validation", "overflow", x)} />
        <Bool label="Spelling check" val={v.spelling} set={(x) => upd("validation", "spelling", x)} />
        <Bool label="Require manual approval" val={v.manual_approval} set={(x) => upd("validation", "manual_approval", x)} />
        <Num label="Programmatic retry count" val={v.retry_count} set={(x) => upd("validation", "retry_count", x)} />
      </Section>
    </div>
  );
}
